import type { AxiosResponse } from 'axios'
import axios from 'axios'
import { $getDeviceId, $getToken, $removeToken } from '@/helpers'
import { useToastStore } from '@/stores'
import { asyncTrack } from '@/track'
import { brand } from '@/config/brands'
import { getClientGroupName } from '@/config/brands/clientGroup'
import i18n, { LOCALE_MAP } from '@/i18n'

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE,
  responseType: 'json',
  // 兜底超时:无超时的请求挂起会卡死路由守卫(login 在守卫内 await),导致整页白屏
  timeout: 15000,
})

function createTrackId() {
  const random6 = Math.floor(Math.random() * 900000 + 100000).toString()
  const env = import.meta.env.MODE.includes('production') ? 'p' : 't'
  return `${env}_${brand}_h5_${new Date().getTime()}_${random6}`
}

function httpCostTrack(response: any, elapsed: number) {
  asyncTrack('tech_http_cost', {
    http_url: response.request.responseURL,
    response_code: response.status,
    request_type: 'api',
    cost_ms: elapsed,
    xy_trace_id: response.config.headers.xy_trace_id,
    page_title: document.title,
  })
}

function httpErrorTrack(response: any) {
  const errorInfo = response.status !== 200 ? response.statusText : response.data.msg
  const errorCode = response.status !== 200 ? response.status : response.data.code
  asyncTrack('tech_page_error', {
    err_info: errorInfo,
    err_type: 'request_error',
    err_title: document.title,
    err_code: errorCode,
    err_page: window.location.href,
    err_url: response.request.responseURL,
    xy_trace_id: response.config.headers.xy_trace_id,
  })
}

const requestTiming: any = {}
function getRequestTiming(response: AxiosResponse<any, any>) {
  const start = requestTiming[response.config.headers.xy_trace_id]
  if (start) {
    const end = Date.now()
    const elapsed = end - start
    // eslint-disable-next-line no-console
    console.log(`请求（ID: ${response.config.url}）耗时：${elapsed}ms`)
    delete requestTiming[response.config.headers.xy_trace_id]
    httpCostTrack(response, elapsed)

    if (response.status !== 200 || response.data.code !== 0)
      httpErrorTrack(response)
  }
}

// 请求
api.interceptors.request.use((options) => {
  options.headers.Platform = 3
  options.headers.versionCode = '110' // 随便传一个, 但是一定要有
  options.headers.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  options.headers.xy_trace_id = createTrackId()
  options.headers.clientGroupName = getClientGroupName()
  options.headers.contentType = '4'
  options.headers.language = LOCALE_MAP[i18n.locale as keyof typeof LOCALE_MAP] || LOCALE_MAP.en

  requestTiming[options.headers.xy_trace_id] = Date.now()

  // Skip `development` mode, Because of CORS
  if (import.meta.env.MODE !== 'development') {
    if (options.url?.startsWith('/tracking/'))
      options.baseURL = `/${import.meta.env.VITE_TRACK_PREFIX}`
    else
      options.baseURL = import.meta.env.VITE_API_BASE
  }
  else {
    if (options.url?.startsWith('/tracking/'))
      options.baseURL = 'https://tracking-api-test.rocnovel.com'
    else
      options.baseURL = 'https://story-api-test.rocnovel.com'
  }

  const token = options.headers?.token || $getToken()
  if (token)
    options.headers.Authorization = token

  const equipmentId = options.headers?.equipmentId || $getDeviceId()
  if (equipmentId)
    options.headers.equipmentId = equipmentId

  options.params = {
    ...options.params,
  }

  return options
})

// 响应
api.interceptors.response.use((response) => {
  getRequestTiming(response)

  // 用户 TOKEN 过期
  if (response.data.code === 401) {
    $removeToken()

    // 原地刷新一次让守卫重新登录,保留当前页面(投放章节)上下文;sessionStorage 防循环,二次仍 401 才回首页
    if (!window.sessionStorage.getItem('TOKEN_EXPIRED_RELOADED')) {
      window.sessionStorage.setItem('TOKEN_EXPIRED_RELOADED', '1')
      window.location.reload()
    }
    else {
      useToastStore().$toast('User info has expired, the page is about to refresh.')
      setTimeout(() => {
        window.location.replace('/')
      }, 2000)
    }
    return
  }

  if (response.data.code !== 0)
    return Promise.reject(response.data)

  // 请求恢复正常后清除 401 刷新标记,让下一次过期仍能走无感刷新
  if (window.sessionStorage.getItem('TOKEN_EXPIRED_RELOADED'))
    window.sessionStorage.removeItem('TOKEN_EXPIRED_RELOADED')

  return response.data
})
